import { Container, Graphics } from "pixi.js";
import { paintSelection, type SelectionChrome } from "./CardView.ts";
import { UI_SELECTION } from "./uiColors.ts";

/**
 * Radio de las esquinas, en px de MUNDO: escala con el zoom como el resto del
 * contenido, igual que el de las tarjetas. Antes se compensaba con el zoom
 * para quedar fijo en pantalla, y el resultado era que la esquina cambiaba de
 * forma respecto del recuadro mientras se acercaba o alejaba.
 *
 * Es el mismo valor que la pastilla del nombre (`.group-label` en el CSS), así
 * que con el título a escala de mundo (lo normal) las dos esquinas calzan en
 * cualquier zoom; con el título fijo, sólo al 100%. El grosor del borde sí
 * sigue fijo en pantalla: es un hairline de interfaz, como la marquesina.
 */
const GROUP_RADIUS = 6;

/**
 * Azul de selección (el mismo de los handles de una tarjeta). Se usa cuando el
 * grupo es el destino de un arrastre: el recuadro entero se pinta de este color
 * y engorda a 2 px, que es la señal de "soltá acá y esto queda adentro".
 */
const DROP_COLOR = UI_SELECTION;
const DROP_WIDTH = 2;

/**
 * El recuadro de grupo: un hairline sólido de un píxel físico (no punteado)
 * sobre un relleno muy tenue del color del grupo.
 *
 * El nombre NO se dibuja acá: vive en el DOM, en `GroupLabels`. Este recuadro
 * tiene que quedar por debajo de las tarjetas y la etiqueta por encima de todo,
 * así que no pueden compartir capa.
 */
export class GroupView {
  readonly container = new Container();
  private bg = new Graphics();
  private outline = new Graphics();
  /**
   * Los agarres del contenedor van en su propia capa porque son azules de
   * selección y el recuadro es del color del grupo: si compartieran `Graphics`
   * habría que repintar los dos cada vez que cambia cualquiera de los dos.
   */
  private selection = new Graphics();

  /** Lo último dibujado, para no repintar un vector que no cambió. */
  private lastKey = "";
  private lastSelKey = "";

  constructor() {
    this.container.eventMode = "none";
    this.container.addChild(this.bg);
    this.container.addChild(this.outline);
    this.container.addChild(this.selection);
  }

  update(
    x: number,
    y: number,
    width: number,
    height: number,
    zoom: number,
    selected: boolean,
    color: number,
    /** El arrastre en curso terminaría soltando las tarjetas acá adentro. */
    dropTarget = false,
    chrome: SelectionChrome = {},
  ): void {
    this.container.position.set(x, y);

    const paint = dropTarget ? DROP_COLOR : color;

    // Sólo se repinta lo que cambió, como en `ShapeView`: antes los dos
    // `Graphics` se borraban y re-armaban en cada frame, aunque el lienzo
    // estuviera quieto o se estuviera sólo desplazando. El zoom entra en la
    // firma sólo como destino de arrastre: es el único borde que se compensa
    // con él (ver abajo), así que en el caso normal hacer zoom no repinta nada.
    const key =
      `${width}|${height}|${paint}|${selected ? 1 : 0}` + (dropTarget ? `|drop|${zoom}` : "");
    if (key !== this.lastKey) {
      this.lastKey = key;
      const radius = Math.min(GROUP_RADIUS, width / 2, height / 2);

      this.bg.clear();
      this.bg.roundRect(0, 0, width, height, radius);
      this.bg.fill({ color: paint, alpha: dropTarget ? 0.16 : selected ? 0.12 : 0.06 });

      this.outline.clear();
      this.outline.roundRect(0, 0, width, height, radius);
      if (dropTarget) {
        // `pixelLine` sólo dibuja 1 px, y el destino necesita engordar para
        // leerse como "soltá acá": se compensa con el zoom, y sólo dura lo
        // que dura el arrastre.
        this.outline.stroke({
          width: DROP_WIDTH / zoom,
          color: paint,
          alpha: 1,
          alignment: 0.5,
        });
      } else {
        // Una línea de un píxel físico a cualquier escala, resuelta por la GPU:
        // no depende del zoom, así que no hay que repintarla al acercar o
        // alejar. En una pantalla Retina es medio px de CSS, más fina que el
        // hairline de 1 px que se compensaba a mano.
        this.outline.stroke({ width: 1, pixelLine: true, color: paint, alpha: 0.9 });
      }
    }

    // Seleccionado, el contenedor muestra los mismos ocho agarres que una
    // tarjeta. Antes no dibujaba ninguno —el borde se agarraba a ciegas— y era
    // el caso donde más se notaba tener que cazar el punto exacto.
    const showHandles = selected && chrome.handles;
    const selKey = showHandles ? `${width}|${height}|${zoom}|${chrome.hover ?? ""}` : "";
    if (selKey !== this.lastSelKey) {
      this.lastSelKey = selKey;
      if (showHandles) {
        paintSelection(this.selection, width, height, zoom, { ...chrome, outline: false });
      } else {
        this.selection.clear();
      }
    }
  }

  destroy(): void {
    this.container.removeFromParent();
    this.bg.destroy();
    this.outline.destroy();
    this.selection.destroy();
    this.container.destroy();
  }
}
